import React, {
	memo,
	useCallback,
	useEffect,
	useState
} from 'react';
import {
	useHistory,
	useLocation
} from 'react-router-dom';
import PropTypes from 'prop-types';
import { Message } from 'semantic-ui-react';

import {
	handleError,
	log
} from '../../../services/utils';

import useForm from '../../../hooks/useForm';
import useToggle from '../../../hooks/useToggle';

import {
	twoFactorAuthenticate,
	getUserProfile,
	resendTwoAuthMethod
} from '../../../api/nora/auth';

import { useAuthAction } from '../../../contexts/AuthContext';
import { useUIAction } from '../../../contexts/UIContext';

import AuthInput from '../AuthInput';
import SubmitButton from '../SubmitButton';
import TextButton from '../../Shared/TextButton';
import Alert from '../../Alert';

import styles from '../auth.module.css';

const TwoFactorForm = ( {
	method,
	setMethod
} ) => {
	const history = useHistory();
	const {
		state: {
			credentials,
			userID
		}
	} = useLocation();

	const { signIn } = useAuthAction();
	const { setScreenLoader } = useUIAction();

	const {
		formData,
		updateField,
		setFormData
	} = useForm( {} );

	const [ errorModal, toggleErrorModal ] = useToggle( false );
	const [ errorModalMessage, setErrorModalMessage ] = useState( '' );
	const [ resendSuccessModal, toggleResendSuccessModal ] = useToggle( false );

	// if no login credentials are found, navigate back to login screen
	useEffect( () => {
		if ( !credentials ) {
			history.push( '/login' );
		} else {
			setFormData( {
				code: '',
				userName: credentials.userName
			} );
		}
	}, [
		credentials,
		history,
		setFormData
	] );

	const handleSubmit = useCallback( async () => {
		try {
			setScreenLoader( 'Authorizing Access...' );
			const {
				token,
				userID,
				valid,
				portal
			} = await twoFactorAuthenticate( formData );

			if ( valid ) {
				if ( token ) {
					sessionStorage.setItem( 'token', token );
				}

				if ( userID ) {
					const userProfile = await getUserProfile( userID );

					if ( userProfile ) {
						// the following sets the user in authContext which will redirect automatically to the main routes
						signIn( {
							...userProfile,
							portal
						} );
					} else {
						throw new Error( 'userProfile not found' );
					}
				} else {
					throw new Error( 'userID not found' );
				}
			} else {
				throw new Error( 'Unable to Authorize' );
			}

			setScreenLoader( '' );
		} catch ( error ) {
			setScreenLoader( '' );
			const errorMessage = handleError( error );

			setErrorModalMessage( errorMessage );
			toggleErrorModal();
		}
	}, [
		formData,
		setScreenLoader,
		signIn,
		toggleErrorModal
	] );

	const resendCode = useCallback( async () => {
		try {
			await resendTwoAuthMethod( userID, method );
			toggleResendSuccessModal();
		} catch ( error ) {
			const errorMessage = handleError( error );

			setErrorModalMessage( errorMessage );
			toggleErrorModal();
		}
	}, [
		method,
		toggleErrorModal,
		toggleResendSuccessModal,
		userID
	] );

	const handleKeyDown = useCallback( e => {
		if ( e.key === 'Enter' ) {
			handleSubmit();
		}
	}, [ handleSubmit ] );

	const handleNewMethod = useCallback( async () => {
		let newMethod;

		setMethod( prev => {
			let method = prev === 1 ? 2 : 1;

			newMethod = method;

			return method;
		} );

		try {
			await resendTwoAuthMethod( userID, newMethod );
			toggleResendSuccessModal();
		} catch ( error ) {
			const errorMessage = handleError( error );

			setErrorModalMessage( errorMessage );
			toggleErrorModal();
		}
	}, [
		setMethod,
		toggleErrorModal,
		toggleResendSuccessModal,
		userID
	] );

	if ( !credentials ) {
		return null;
	}

	return (
		<>
			{ log( 'twoFactorForm' ) }
			<div className={ styles.inputs }>
				<AuthInput
					icon="lock"
					name="code"
					onChange={ updateField }
					onKeyDown={ handleKeyDown }
					placeholder="Authentication Code"
					value={ formData.code }
				/>
			</div>
			<Message className={ styles.message }>An Authentication Code has been sent to the email associated with this account. Please enter that code above.</Message>
			<SubmitButton
				buttonText="Authorize Access"
				onSubmit={ handleSubmit }
			/>
			<TextButton
				buttonText="Resend Code"
				className={ styles.textButton }
				onClick={ resendCode }
			/>
			<TextButton
				buttonText={ `Not working? Try ${ method === 1 ? 'sms' : 'email' }.`}
				className={ styles.textButton }
				onClick={ handleNewMethod }
			/>
			<Alert
				name="two-factor-error-alert"
				heading="Unable to Process Request"
				message={ errorModalMessage }
				open={ errorModal }
				closeModal={ toggleErrorModal }
				type="error"
			/>
			<Alert
				name="resend-success-alert"
				heading="New Code Sent"
				message={ `A new code was sent to your ${ method === 1 ? 'email' : 'phone' }!` }
				open={ resendSuccessModal }
				closeModal={ toggleResendSuccessModal }
				type="success"
			/>
		</>
	);
};

TwoFactorForm.propTypes = {
	method: PropTypes.number,
	setMethod: PropTypes.func
};

export default memo( TwoFactorForm );
